import React, { useContext, useEffect, useState } from "react";
import { Card, CardBody, Button, Spinner } from "reactstrap";
import { PostData } from "../../../services/FetchData";
import SemanticSearchSettings, {
	formatSemanticSourceFieldsText,
	parseSemanticSourceFieldsText,
} from "../../../components/Drupal/SemanticSearchSettings";
import { ProductContext } from "../../../store";
import { toast } from "react-toastify";

const DRUPAL_BASE = "ms3";

/**
 * Shared Drupal semantic + NL query settings (autocomplete, instant search, search results).
 */
export default function DrupalSemanticSearchSettingsPage() {
	const { currentWebsiteId } = useContext(ProductContext);
	const websiteId = currentWebsiteId;
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [instantSearchConfig, setInstantSearchConfig] = useState(null);
	const [semanticSearchEnabled, setSemanticSearchEnabled] = useState(false);
	const [semanticSourceFieldsText, setSemanticSourceFieldsText] = useState("product_title, body");
	const [semanticHybridAlpha, setSemanticHybridAlpha] = useState(0.3);
	const [nlQueryEnabled, setNlQueryEnabled] = useState(false);
	const [nlModelId, setNlModelId] = useState("");
	const [keywordMatchMode, setKeywordMatchMode] = useState("strict");

	useEffect(() => {
		if (!websiteId) return;
		PostData(DRUPAL_BASE, "api/v1/drupal/getInstantSearchConfig", { websiteId })
			.then((res) => {
				const isc = res?.data?.instantSearchConfig || res?.instantSearchConfig || {};
				setInstantSearchConfig(isc);
				setSemanticSearchEnabled(Boolean(isc.semanticSearchEnabled));
				setSemanticSourceFieldsText(formatSemanticSourceFieldsText(isc.semanticSourceFields));
				setSemanticHybridAlpha(isc.semanticHybridAlpha ?? 0.3);
				setNlQueryEnabled(Boolean(isc.nlQueryEnabled));
				setNlModelId(isc.nlModelId || "");
				setKeywordMatchMode(isc.keywordMatchMode === "loose" ? "loose" : "strict");
			})
			.catch((err) => toast.error(err?.message || "Failed to load config"))
			.finally(() => setLoading(false));
	}, [websiteId]);

	async function handleSave(e) {
		e.preventDefault();
		if (!websiteId) return;
		const fields = parseSemanticSourceFieldsText(semanticSourceFieldsText);
		if (semanticSearchEnabled && fields.length === 0) {
			toast.error("Enter at least one field name for semantic search.");
			return;
		}
		setSaving(true);
		try {
			const base = instantSearchConfig && typeof instantSearchConfig === "object" ? instantSearchConfig : {};
			const result = await PostData(DRUPAL_BASE, "api/v1/drupal/saveInstantSearch", {
				websiteId,
				instantSearchConfig: {
					...base,
					semanticSearchEnabled,
					semanticSourceFields: fields.length ? fields : ["product_title", "body"],
					semanticHybridAlpha,
					nlQueryEnabled,
					nlModelId: nlModelId.trim() || null,
					keywordMatchMode: keywordMatchMode === "loose" ? "loose" : "strict",
				},
			});
			if (result?.status === "success") {
				toast.success(result.message || "Saved");
			} else {
				toast.success("Saved");
			}
		} catch (err) {
			toast.error(err?.message || "Failed to save");
		} finally {
			setSaving(false);
		}
	}

	if (loading) return <Spinner />;
	if (!websiteId) return <p>Select a website.</p>;

	return (
		<Card>
			<CardBody>
				<form onSubmit={handleSave}>
					<h2 className="h5 mb-2">Semantic Search</h2>
					<p className="text-muted small mb-0">
						These settings apply to <strong>Autocomplete</strong>, <strong>Instant Search</strong>, and the
						{" "}<strong>search results page</strong>. Configuration is applied server-side — no Drupal module
						changes required.
					</p>

					<SemanticSearchSettings
						semanticSearchEnabled={semanticSearchEnabled}
						onSemanticSearchEnabledChange={setSemanticSearchEnabled}
						semanticSourceFieldsText={semanticSourceFieldsText}
						onSemanticSourceFieldsTextChange={setSemanticSourceFieldsText}
						semanticHybridAlpha={semanticHybridAlpha}
						onSemanticHybridAlphaChange={setSemanticHybridAlpha}
						nlQueryEnabled={nlQueryEnabled}
						onNlQueryEnabledChange={setNlQueryEnabled}
						nlModelId={nlModelId}
						onNlModelIdChange={setNlModelId}
						keywordMatchMode={keywordMatchMode}
						onKeywordMatchModeChange={setKeywordMatchMode}
					/>

					<Button type="submit" color="primary" className="mt-4" disabled={saving}>
						{saving ? "Saving..." : "Save Semantic Search Settings"}
					</Button>
				</form>
			</CardBody>
		</Card>
	);
}
